import { createSlice } from '@reduxjs/toolkit';

/**
 * CACHE CONFIGURATION:
 * Master data stays cached in Redux memory for 10 minutes (600,000 ms).
 * Revisiting a step within 10 minutes costs 0 network calls (instant 0ms response).
 */
const CACHE_TTL_MS = 10 * 60 * 1000;

const initialState = {
  cand: {},          // Structure: { step0: {...}, step1: {...}, step2: {...} }
  loadingSteps: {},  // Structure: { 'cand_0': false, 'cand_1': true }
  lastFetched: {},   // Structure: { 'cand_0': 1721234567890 }
};

const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    /** Stores fetched data for a specific form type + step number */
    setStepData: (state, action) => {
      const { formType, step, data } = action.payload;
      if (!state[formType]) {
        state[formType] = {};
      }
      state[formType][`step${step}`] = data;
      state.lastFetched[`${formType}_${step}`] = Date.now();
      state.loadingSteps[`${formType}_${step}`] = false;
    },

    /** Marks a step as loading while the API request is in-flight */
    setStepLoading: (state, action) => {
      const { formType, step, isLoading } = action.payload;
      state.loadingSteps[`${formType}_${step}`] = isLoading;
    },

    /** Clears cache for a specific step (e.g. after adding a new item via Add-on dialog) */
    invalidateStepCache: (state, action) => {
      const { formType, step } = action.payload;
      if (state[formType] && state[formType][`step${step}`]) {
        delete state[formType][`step${step}`];
      }
      delete state.lastFetched[`${formType}_${step}`];
    },
  },
});

export const { setStepData, setStepLoading, invalidateStepCache } = masterDataSlice.actions;

// ─── SELECTORS ───────────────────────────────────────────────────────────────

/** Returns cached data object for (formType, step), or null if not cached */
export const selectStepData = (formType, step) => (state) =>
  state.masterData?.[formType]?.[`step${step}`] ?? null;

/** Returns true if an API call is currently fetching data for this step */
export const selectIsStepLoading = (formType, step) => (state) =>
  state.masterData?.loadingSteps?.[`${formType}_${step}`] === true;

/** Returns true if cache exists and is within 10-minute TTL */
export const selectIsCacheValid = (formType, step) => (state) => {
  const timestamp = state.masterData?.lastFetched?.[`${formType}_${step}`];
  if (!timestamp) return false;
  return Date.now() - timestamp < CACHE_TTL_MS;
};

export default masterDataSlice.reducer;